// THE FOUR KINDS OF SUB-STEP. The whole vocabulary, written once: the Phase 4 prompt asks
// for one of these numbers (see stepAnalyzePrompt, which builds its bullet from this list)
// and the sub-step tables print it (see AllSubSteps).
//
// THERE WERE FIVE, and the first of them was "position a single part" — laying the frame
// flat, standing the chair up. It went because of what it produced downstream: Phase 5 draws
// a sub-step as one part arriving on another, and a gesture with nothing to arrive on is a
// lone mesh drifting through empty space. Unwatchable, unjudgeable, and it sat in the phase's
// denominator waiting for an animation that had no reason to exist.
//
// It is not a gesture that got demoted, it is one that was never a unit of assembly: nothing
// is joined, nothing is consumed, and the part it positions is named again by the gesture
// that actually uses it. See the prompt, which now excludes it outright rather than asking
// for it and hiding the answer.
//
// AND THE NUMBERS WERE RE-STRUCK, 1 to 4, rather than left as 2 to 5 with a hole at the top.
// Stored answers from before this carry the old numbering, and the two do not agree — an old
// 3 was "prepare hardware" and a new 3 is "combine a component and a hardware part" — so the
// database is migrated rather than reinterpreted (see database.py, substep_kinds_renumbered).
// One integer, one meaning, everywhere it is stored.
//
// WHY A CLOSED LIST AT ALL. A sub-step was defined as "one single physical action", which
// is a definition with no edge. This came back as one:
//
//   "Attach the side rail (C) to the backrest and rear legs frame (A) using 2 JCBC screws
//    (E) and 2 spring washers (F), without tightening fully, using the Allen key (I)."
//
// One sentence, one verb, and four gestures — two of them making a fastener out of two
// pieces of hardware before anything is screwed to anything at all. Nothing in "one single
// physical action" says that is too much, because the phrase has no unit in it.
//
// The three kinds are that unit. Each names what it TAKES — how many component parts, how
// many hardware parts — so an action that does not fit exactly one of them is more than one
// sub-step, and the count says where to cut. That is a test a reader can apply to a sentence,
// which "atomic" never was.
//
// TWO PARTS, AT MOST, IN ANY OF THEM, and the cap is what makes the list a list of gestures
// rather than of sentences. A TOOL IS NOT COUNTED: it goes with the gesture it is used in — a
// barrel nut put into a rail and turned with the screwdriver is one gesture. It was counted, and
// a fourth kind took the tool's turn as a sub-step of its own ("Tighten the JCBC screw (E)",
// never saying with what); Louis had it folded back into the insertion, from Phase 4 on and with
// no recombining afterwards, on 2026-10-17. A tool coming back alone to a fastener already in
// place is no sub-step now, as turning a part over is none.
//
// TWO FAMILIES over the five: PLACING moves pieces and fastens nothing; COMBINING fastens.
// Derived from the number rather than stored beside it — one fact, in one place, and a
// sub-step cannot come back a placing 5.
//
// THE FAMILY IS ABOUT FASTENING AND NOTHING ELSE. It used to decide much more: a placing left
// no piece, consumed nothing, and its parts stayed on the table as themselves. All of that
// followed from "it fastens nothing", and none of it survives the question "what is on the
// table after this gesture" — a side rail held against a frame is one object, and the next
// gesture works on that object rather than on a rail and a frame that happen to be touching.
//
// So every kind leaves a piece and consumes what it names, and the family says which ones a
// screwdriver was involved in. That is a real distinction — Phase 5 draws a fitting
// differently from a placement — and it is now the only one riding on it.
//
// THESE TWO FAMILIES ARE THE ONLY PLACING/COMBINING THERE IS. `SubStepFitting` carried a
// `kind` of its own — 'fitting' or 'placing', asked of Phase 5's movement call — which was
// this same distinction answered a second time by a second reader. Two fields for one fact
// is two fields that disagree, and they could: a sub-step classified a combining, driving a
// fastener home, beside a movement calling itself a placing.
//
// It is gone. Phase 5 reads the family off the number below, and where the number is null —
// a manual whose steps predate the kinds — off the movement's own ids, which say the same
// thing the longer way: something to be fitted ONTO is a combining. See assemblySlots, the
// one place that decides it.

// WHERE THE IDS OF THE PIECES THESE KINDS MAKE START. A supplied part's id is its position in
// Phase 2's list — counted from one, rarely past thirty; a piece a combining leaves takes the
// next hundred. Two ranges, no arithmetic to tell them apart, and a reader who sees "101" knows
// before reading the name that nobody supplied it.
//
// It lives with the kinds because it belongs to what they LEAVE, and because the three modules
// that need it — the prompt that asks for the numbering, the run that says where it stands, and
// the table that assigns the same numbers as it draws them — would otherwise have to import it
// from each other in a ring.
export const MADE_ID_BASE = 101;

export type SubStepKind = 1 | 2 | 3;
export type SubStepFamily = 'placing' | 'combining';

export type SubStepKindDef = {
  kind: SubStepKind;
  family: SubStepFamily;
  // What the gesture is, in the prompt's own words — English, because the prompt is
  // English. The reader's two languages are in LanguageContext, under `labelKey`.
  what: string;
  // AND IN ONE WORD, for a prompt that lists every gesture of the manual and defines the words
  // once at its head (see hardwareInteractionsPrompt) — `what` thirty-five times over was most
  // of that list.
  short: string;
  // What it takes, and what is left when it is done. EVERY KIND LEAVES SOMETHING: a gesture
  // that puts two parts together has left a thing that was not on the table before, whether or
  // not a fastener was involved, and the manual's next gesture works on that thing. `makes` was
  // empty for the placings while a placing was held to leave nothing — see the head of this
  // file for what that cost.
  takes: string;
  makes: string;
  // WHETHER THIS GESTURE CAN PUT HARDWARE INTO WOOD, and so whether a hole is involved in it.
  //
  // DECLARED HERE RATHER THAN READ OFF `takes`, though it follows from it: a gesture naming a
  // component part and a piece of hardware is a gesture with a bore in it. Deriving it would
  // mean matching on the wording of a sentence written for a prompt, which is a string this
  // file is free to reword and every reader of it is not.
  //
  // AND DECLARED HERE RATHER THAN LISTED WHERE IT IS USED. Phase 4's hole call asks only about
  // the gestures this is true of — it does not ask a model which gestures those are, having
  // already been told what each gesture IS — and a caller testing `kind === 3 || kind === 4`
  // would be a second copy of the vocabulary, in the app that renumbered these once already.
  // See the head of this file, where the same argument is made about `makesHardware`.
  fillsHole: boolean;
  labelKey: string;
  // WHETHER PHASE 4 IS STILL OFFERED IT. False on "prepare hardware" since 2026-10-21: a washer
  // slid onto a screw is part of the gesture putting that screw in (see the prompt's "prepared"
  // role). Kept, so the answers on disk still read as what they are.
  asked?: boolean;
};

export const SUB_STEP_KINDS: readonly SubStepKindDef[] = [
  {
    kind: 1,
    family: 'placing',
    what: 'position a part relative to another part',
    short: 'position',
    takes: '2 component parts',
    makes: '1 positioned assembly',
    // Two pieces of wood held against each other. Nothing goes into anything.
    fillsHole: false,
    labelKey: 'notice.steps.kind.1',
  },
  {
    kind: 2,
    family: 'combining',
    what: 'prepare hardware',
    short: 'prepare',
    takes: '2 hardware parts',
    makes: '1 assembled hardware part',
    // A washer onto a screw. It happens in the hand, nowhere near a part.
    fillsHole: false,
    labelKey: 'notice.steps.kind.2',
    asked: false,
  },
  {
    kind: 3,
    family: 'combining',
    what: 'combine a component part and a hardware part, and drive or adjust that hardware with '
      + 'its tool where the drawing uses one on it',
    short: 'combine',
    takes: '1 component part, 1 hardware part, and the tool that drives it if one is used',
    makes: '1 assembled component part',
    // THE ONE THAT REVEALS A BORE. A dowel into a rail, a cam into a panel, a screw through
    // one part into another — every hole this app knows about is first known about here.
    //
    // AND IT TAKES THE TOOL THAT DRIVES WHAT IT PUTS IN: the screw goes in and the key turns it,
    // one gesture (see the head of this file).
    fillsHole: true,
    labelKey: 'notice.steps.kind.3',
  },
] as const;

// The number a stored sub-step carries, or null for anything else — a sub-step analysed
// before the kinds were asked for, and a model answer outside the list. Null is "not
// answered", never a fifth kind: everything that reads this field has to be able to tell a
// gesture nobody classified from one classified wrongly, and it cannot if the field is
// quietly defaulted to a number.
export const asSubStepKind = (raw: unknown): SubStepKind | null => {
  const n = typeof raw === 'number' ? raw : Number(raw);
  return SUB_STEP_KINDS.some((d) => d.kind === n) ? (n as SubStepKind) : null;
};

export const subStepKindDef = (kind: SubStepKind | null | undefined): SubStepKindDef | null =>
  SUB_STEP_KINDS.find((d) => d.kind === kind) ?? null;

// WHETHER WHAT THIS GESTURE LEAVES IS A PIECE OF HARDWARE. One kind makes one — two pieces of
// hardware joined into a fastener — and everything else leaves something you would call
// furniture. Asked here rather than by comparing the number where it is needed, so the one
// file that owns the numbering is the one file a renumbering has to be right in: the chip
// column that reads this was a bare `kind === 3` on the day the kinds were 1 to 5, and a
// silent renumber would have filed every screwdriver gesture under Hardware.
export const makesHardware = (kind: SubStepKind | null | undefined): boolean =>
  subStepKindDef(kind)?.takes === '2 hardware parts';

// WHETHER A HOLE COULD BE INVOLVED IN THIS GESTURE — see SubStepKindDef.fillsHole, which is
// where the answer is declared per kind.
//
// TRUE FOR A GESTURE NOBODY CLASSIFIED, which is the only defaulting in this file and is the
// safe direction. Null is "not answered": a sub-step analysed before the kinds were asked for
// might be a screw going into a rail, and filtering it out would lose that hole silently. What
// it costs is one extra gesture in front of the call, which can answer nothing for it — the
// hole list is not one entry per gesture.
//
// Which makes ANY kind with no definition true, and not only null. The two are one state — we
// do not know what this gesture is — and a stored kind outside the list cannot reach here
// anyway (see asSubStepKind, which turns one into null at the door). Answering them
// differently would be a distinction with nothing behind it, waiting to be found the day
// something does reach here.
export const fillsHole = (kind: SubStepKind | null | undefined): boolean =>
  (subStepKindDef(kind)?.fillsHole ?? true);

// PLACING or COMBINING, off the number. See the head of this file for why it is not a field.
export const subStepFamily = (kind: SubStepKind | null | undefined): SubStepFamily | null =>
  subStepKindDef(kind)?.family ?? null;

// THE KIND IN WORDS — what the gesture does, said outright: "Screwing two component parts
// together".
//
// IT USED TO BE THE FAMILY AND THE NUMBER, "COMBINING 5", with the sentence hidden in a title
// attribute. That is a code, and a code has to be learnt: nothing on the screen said what the 5
// was one of, so a reader either already knew the list or hovered a cell to find out — and a
// column of them read as five shades of the same two words. The sentence was always the answer;
// it was just filed where only a mouse could reach it. The number is gone from the text with
// it, because the words no longer need an index to tell them apart.
//
// TWO PLACES DRAW THIS and they look nothing alike: a cell in the sub-step table's KIND column,
// and a chip in Phase 5 that spins while its call is out. What they must not differ in is the
// WORDING — one gesture named two ways across two screens is two gestures as far as a reader
// can tell — so the shape and the size stay with each of them, and the words and the colour
// live here.
//
// `family` is the fallback for a sub-step with no kind: Phase 5 can still tell a placing from a
// combining off the movement's ids (see assemblySlots), and where it has, the family word alone
// is the honest answer — there is no sentence to print, because nobody was asked which of the
// five. Null when there is nothing to say at all, which is the caller's cue to draw nothing.
export function subStepKindWords(
  kind: SubStepKind | null | undefined,
  family: SubStepFamily | null | undefined,
  t: (k: string) => string,
): { text: string; family: SubStepFamily } | null {
  const def = subStepKindDef(kind);
  const fam = def?.family ?? family;
  if (!fam) return null;
  return { text: t(def ? def.labelKey : `notice.steps.family.${fam}`), family: fam };
}

// AND THE FAMILY'S COLOUR, since the words are now long enough that the eye reaches the colour
// first. Sky for a placing, green for a combining — Phase 5's own two, from when the chip said
// nothing but the family — so the one thing colour claims is the one distinction the families
// are: does this gesture fasten anything. A colour per kind would have said it once a kind and
// sorted nothing.
export const subStepKindTone = (family: SubStepFamily): string =>
  family === 'combining'
    ? 'bg-emerald-50 text-emerald-700'
    : 'bg-sky-50 text-sky-700';

// The kinds, as the prompt lists them, grouped under their family and indented to the depth
// the surrounding bullets use. Built here rather than typed out there, so the prompt cannot
// come to describe a kind this app does not store — the two used to be one paragraph in one
// file precisely because nobody had to keep them in step, and now something does.
export function subStepKindLines(indent = '     '): string {
  const family = (f: SubStepFamily) =>
    SUB_STEP_KINDS.filter((d) => d.family === f && d.asked !== false)
      .map((d) => {
        const signature = d.makes ? `${d.takes} → ${d.makes}` : d.takes;
        return `${indent}${d.kind} — ${d.what}: ${signature}.`;
      })
      .join('\n');
  return (
    `${indent.slice(2)}PLACING — nothing is fastened:\n${family('placing')}\n` +
    `${indent.slice(2)}COMBINING — pieces are fastened:\n${family('combining')}`
  );
}
